// Story 8.2 — TanStack Query mutation for POST /api/v1/wallet/earnings/convert.
//
// Moves creator revenue share out of the EARNINGS pot and into the spendable
// WALLET pot. One way, free.
//
// There is deliberately no `useConvertBalanceToEarnings`. Purchased balance
// becoming payable is the one thing the closed-loop model cannot allow, so the
// inverse has no endpoint, no hook and no admin equivalent — see
// `server/src/services/outward_value.rs` and its CI guard
// `no_ledger_path_from_wallet_pot_to_external`.
//
// Per-submit idempotency key (regenerated per submit, NOT per modal mount —
// the Stories 1.16/1.17/1.18 pattern): a network retry must not double-convert.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ConvertEarningsResponse = {
  converted_cents: number;
  /** The EARNINGS pot after the move. */
  earnings_cents: number;
  /** The WALLET pot after the move. Never a sum of the two. */
  balance_cents: number;
};

export type ConvertEarningsError = {
  code: string;
  message: string;
  field?: string;
  details?: { required_cents?: number; available_cents?: number };
};

async function postConvertEarnings(
  amountCents: number,
  idempotencyKey: string,
): Promise<ConvertEarningsResponse> {
  const response = await fetch("/api/v1/wallet/earnings/convert", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify({ amount_cents: amountCents }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: ConvertEarningsError = {
      code: "CONVERT_FAILED",
      message: `Could not move your earnings (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  return (await response.json()) as ConvertEarningsResponse;
}

export function useConvertEarnings() {
  const queryClient = useQueryClient();
  return useMutation<
    ConvertEarningsResponse,
    ConvertEarningsError,
    { amountCents: number; idempotencyKey: string }
  >({
    mutationFn: ({ amountCents, idempotencyKey }) =>
      postConvertEarnings(amountCents, idempotencyKey),
    onSuccess: () => {
      // Both pots moved, and the history gained two rows (one per pot).
      queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
      queryClient.invalidateQueries({ queryKey: ["wallet-history"] });
    },
  });
}
